import { LoyaltyChallengeConfig } from '@shared/models';

import {
  DeviceType,
  DISPLAY_CONDITION,
  NavbarItemLocation,
  ProductCheckTabType,
  RewardFormat,
  SubType,
  Variants,
} from '../utils';

export const PONA_CONFIG: LoyaltyChallengeConfig = {
  appIcon: 'assets/images/pona/favicon.ico',
  cssVariables: {
    'font-family': 'Tesco-Modern-Regular',
    'font-family-bold': 'Tesco-Modern-Bold',
    'font-family-light': 'Tesco-Modern-Light',
    'font-family-medium': 'Tesco-Modern-Medium',

    'navbar-height': '73px',
    'navbar-background-color': 'white',

    'nav-link-color': '#333333',
    'nav-link-font-size': '13px',
    'nav-link-active-color': '#00529F',

    'nav-item-bg-color': 'white',
    'nav-item-active-bg-color': 'white',
    'nav-item-hover-bg-color': 'rgb(0 0 0 / 10%)',
    'nav-item-font-family': 'var(--font-family-bold)',

    'body-color': '#00529F',
    'body-background-color': '#00529F',

    'modal-border-radius': '10px',
    'challenge-primary-color': '#00529F',

    'tooltip-text-color': 'white',
    'tooltip-background-color': 'black',
  },
  currency: 'GBP',
  rewardTemplate: '{points}',
  rewardFormat: RewardFormat.POINTS,
  og: {
    title: 'Tesco Clubcard Challenges | Tesco',
    description:
      "Get ready to play Clubcard Challenges. Pick up to 10 challenges from a selection of brands you love, shop in-store or online to hit your challenges' targets, and collect even more Clubcard points with every completed challenge.",
  },
  pages: {
    main: {
      background: {
        mobile:
          'center / cover no-repeat url("assets/images/pona/cover-bg-mobile.webp")',
        tablet:
          'center / cover no-repeat url("assets/images/pona/cover-bg-tablet.webp")',
        desktop:
          'center / cover no-repeat url("assets/images/pona/cover-bg-desktop.webp")',
      },
    },
    availablePages: ['help', 'current'],
    help: {
      meta: {
        title: 'Tesco Clubcard Challenges | Tesco | Help',
        keywords:
          'Tesco, Clubcard, Challenges, Tesco Clubcard Challenges, Tesco Clubcard, Tesco Challenges, Terms and Conditions',
        description:
          "Get ready to play Clubcard Challenges. Pick up to 10 challenges from a selection of brands you love, shop in-store or online to hit your challenges' targets, and collect even more Clubcard points with every completed challenge.",
      },
      header: {
        background:
          'center / cover no-repeat url("assets/images/pona/cover-bg-desktop.webp")',
        borderBottom: '1px solid #eaeaea',
        bannerAlternativeText: '',
        goBackButton: {
          text: 'Go Back To My Challenges',
          backgroundColor: '#00539F',
          color: 'white',
          fontFamily: 'var(--font-family-bold)',
          borderRadius: '60px',
        },
        howToPlay: {
          specificDisplayCondition: DISPLAY_CONDITION.ALWAYS,
          title: {
            color: 'white',
            fontFamily: 'var(--font-family-bold)',
            text: '<span style="font-family: var(--font-family-light); font-size: 30px;">Get ready to play</span> <br> <span style="font-size: 40px; line-height: 35px;">Clubcard Challenges</span>',
          },
        },
      },
      faqVariant: {
        variant: Variants.VARIANT_2,
        title: {
          label: 'Tesco Clubcard Challenges Terms and Conditions',
          styles: {
            color: '#333',
            fontSize: '25px',
            lineHeightt: '30px',
            textAlign: 'center',
            textDecoration: 'underline',
            fontFamily: 'var(--font-family-medium)',
          },
        },
        questions: [
          {
            content:
              '1. The Clubcard Challenges campaign is open to all UK residents who are invited to take part (England, Scotland, Wales, and Northern Ireland) aged 18 or over with a valid Clubcard account, and who are a Tesco app user.<span style="font-style: italic;"> Customers must opt in by following the instructions in the invitation email and opting in on the Clubcard Challenges website to be able to participate.</span>',
          },
          {
            content:
              '2. Clubcard Challenges allows customers the chance to collect up to 5,000 Clubcard points by taking part in specific purchase challenges in-store or via Tesco Grocery online shopping. These challenges require customers to purchase items or brands that we believe are the most relevant to them based on their previous purchases. The number of points collected will vary according to the number of challenges completed, up to a maximum of 5,000 Clubcard points.',
          },
          {
            content:
              '3. Customers can select up to 10 challenges throughout the promotional period to take part in from a list of 20. Each challenge will clearly state the number of Clubcard points customers could collect at 3 different spend thresholds, including the cumulative total amount if customers complete it successfully. Once customers have made a purchase relevant to a challenge and within the campaign timeframe, with a Clubcard either in-store or online linked to their Clubcard account, it will be counted, and the challenge will remain active until it is completed or has expired.',
          },
          {
            content:
              '4. If you use a Clubcard voucher, coupons or a 10% off Clubcard Plus big shop coupon on your shop, the discount value is pro-rated across each item purchased. The qualifying spend towards your Clubcard Challenge will be the discounted value after your voucher, coupon or Clubcard Plus big shop coupon has been applied.',
          },
          {
            content:
              '5. The campaign duration is from 00:01am on {variable} to 23:59 on {variable}. Only purchases completed within this period will count towards collecting any Clubcard points associated with the challenge.',
          },
          {
            content:
              '6. A Clubcard account is required, and customers will need to scan their Clubcard at the checkout in-store or link their Clubcard account to their online order for the transaction to be counted. Purchases from Tesco Petrol Filling Stations are not included.',
          },
          {
            content:
              '7. For purchases to qualify and/or contribute towards the Clubcard Challenges, they must be made by the customer who was invited to participate in Clubcard Challenges, using their Clubcard. Purchases made by other customers, such as, but not limited to, those whose accounts are linked, those who are related to the invitee, or other Clubcard members in the same household, will not qualify or contribute towards the Clubcard Challenges of others.',
          },
          {
            content:
              '8. Customers can track their performance during the campaign on the personalised link they received in the invitation email. This personal page will be updated within 96 hours of each in-store purchase. If you have made an online order for collection or delivery, the personal page will be updated within 96 hours after your collection or delivery has taken place. For further help, please call Customer Services on 0800 591 688, Monday-Friday, 8am-8pm, and Saturday 9am–6pm, or send us a message at WhatsApp on 0800 9177 403 Monday–Saturday 7am–11pm.',
          },
          {
            content:
              '9. If a customer selects multiple challenges that include the same product, the purchase will qualify for both challenges.',
          },
          {
            content:
              '10. All challenges will clearly state which products are included and most will include a list of qualifying items. Customers can view the list by selecting the scan icon on the challenge and clicking on to ‘Product List’. All products included are subject to availability. Products excluded from all Clubcard Challenges include tobacco or tobacco related products, lottery, stamps, prescription medicines, infant formulae milk, Tesco gift cards, third party gift cards, in-store concessions, alcohol, and loose products sold as variable weight. When a Clubcard Challenge requires customers to purchase from specific brands, the eligible product list is limited to only those branded products that are listed in the ‘Product List’ at the time the Clubcard Challenge launches. If a brand launches a new product after their Clubcard Challenge is already underway, it may be added to the ‘Product List’ if it is an eligible product. Products that are High Fat, Sugar or Salt (HFSS) are always excluded from Clubcard Challenges.',
          },
          {
            content:
              '11. Some Clubcard Challenges will include gift cards as their qualifying items. Where gift cards are the qualifying items for a challenge, the Tesco Gift Card terms and conditions apply: Tesco’s change of mind policy does not apply to gift cards and gift cards cannot be returned or refunded. For Tesco’s gift card change of mind policy, please see: FAQ : Returning gift cards - Tesco.com. Your legal rights are not affected. Selected Tesco stores only. Subject to availability. Read the T&Cs on the card before you buy. The ‘Gift Card’ challenge excludes One4all, Favourite and Choice Gift Cards.',
          },
          {
            content:
              '12. Some Clubcard challenges will only be available to a limited number of customers. This will be clearly indicated on the Clubcard challenges to which a maximum redemption limit applies. Once the maximum number of customers have successfully completed these challenges, they will show as “this challenge was popular, sorry you missed out!” and customers will not be able to continue to the next threshold of those challenges if they are mid-way through those challenges, or select those challenges to start to take part in.',
          },
          {
            content:
              '13. Clubcard Challenges can run alongside existing promotions in-store, in the case of a product being on a promotion price, any minimum spend thresholds are only deemed to be met if the minimum amount is reached on qualifying products after any promotions, coupons, Clubcard Prices, and Clubcard Plus discounts have been applied. It is the final price that you pay for any qualifying products that count towards the Clubcard Challenge.',
          },
          {
            content:
              '14. For colleagues, Clubcard Challenges can run alongside existing promotions in-store. In the case of Colleague Clubcard members, it is always the price displayed before any colleague discount is applied that will count towards the Clubcard Challenge, even if the qualifying products are on a promotional price.',
          },
          {
            content:
              '15. We will email you within 28 days of the end of the campaign to confirm how many Clubcard points you have collected.',
          },
          {
            content:
              '16. Any Clubcard points collected by meeting the first or second threshold of an active challenge will be credited to the Clubcard account within 28 days after the end of the promotional period even if the challenge is not fully completed. Challenges cannot be deselected once any spending towards that challenge has commenced. You can only deselect challenges when spend has not begun.',
          },
          {
            content:
              '17. There is no alternative to the prize stated and we cannot send the Clubcard points to anyone else – only the account linked to the completed challenges.',
          },
          {
            content:
              '18. Where we suspect that an individual has breached these T&Cs, we reserve the right to ask for proof of identity, age and address, and that they are the registered user associated with the account used to participate in the challenges.',
          },
          {
            content:
              '19. We reserve the right to cancel, amend, withdraw, terminate, or temporarily suspend this promotion for whatever reason, with no liability to any entrants or third parties. But if this happens, we will use all reasonable endeavours to avoid consumer disappointment.',
          },
          {
            content:
              '20. The promoter’s decision is final and binding. No correspondence will be entered into.',
          },
          {
            content:
              '21. The application and interpretation of these terms and conditions shall be governed by the law of England and Wales, and the courts of England and Wales shall have exclusive jurisdiction in relation to any dispute concerning them.',
          },
          {
            content:
              '22. The Promoter is Tesco Stores Ltd., Tesco House, Shire Park, Kestrel Way, Welwyn Garden City, AL7 1GA.',
          },
          {
            content:
              '23. <span style="font-style: italic;">The Promoter shall use your data in accordance with the <a href="https://www.tesco.com/zones/privacy-centre" target="_blank" aria-label="Go to Tesco’s Privacy Policy Centre" title="Go to Tesco’s Privacy Centre">Promoter’s privacy policy.</a>. It has been used for this promotion to deliver challenges that are relevant to and rewarding for you.</span>',
          },
        ],
      },
      extraLinks: [
        {
          title: {
            text: 'Need more help?',
            fontFamily: 'var(--font-family-bold)',
          },
          link: {
            color: 'white',
            label: 'Contact us',
            fontFamily: 'var(--font-family)',
            backgroundColor: 'var(--challenge-primary-color)',
            href: 'https://www.tesco.com/help/contact/',
          },
        },
      ],
    },
    currentChallenges: {
      meta: {
        title: 'Tesco Clubcard Challenges | Tesco',
        keywords:
          'Tesco, Clubcard, Challenges, Tesco Clubcard Challenges, Tesco Clubcard, Tesco Challenges',
        description:
          "Get ready to play Clubcard Challenges. Pick up to 10 challenges from a selection of brands you love, shop in-store or online to hit your challenges' targets, and collect even more Clubcard points with every completed challenge.",
      },
      background: 'transparent',
      howDoesitWork: {
        color: '#00529F',
        fontFamily: 'var(--font-family-bold)',
        backgroundColor: 'white',
        containerBackgroundColor: '#00105A',
        text: 'How It Works',
        borderRadius: '65px',
        containerPadding: '2rem 0 2rem',
        modal: {
          title: {
            text: 'How it works',
            color: 'white',
            fontFamily: 'var(--font-family-bold)',
          },
          closeButton: {
            imageUrl: 'assets/images/default/close-button.svg',
            alternativeText: 'Close the tutorial',
            color: 'white',
          },
          skipNowButton: {
            text: 'Skip for now',
            color: '#00529F',
            fontFamily: 'var(--font-family-bold)',
            backgroundColor: 'white',
            borderRadius: '65px',
          },
        },
      },
      howToPlay: {
        specificDisplayCondition: DISPLAY_CONDITION.DEFAULT,
        title: {
          color: 'white',
          fontFamily: 'var(--font-family-bold)',
          text: '<span style="font-family: var(--font-family-light); font-size: 30px;">Get ready to play</span> <br> <span style="font-size: 40px; line-height: 35px;">Clubcard Challenges</span>',
        },
      },
      upButton: {
        backgroundColor: '#00539F',
        content: '^',
      },
    },
    sorry: {
      background:
        'white url("assets/images/pona/sorry-page-image.webp") center center / cover no-repeat',
      content: [
        '<div style="max-width: 400px; margin: 0 auto 2rem;"><img src="assets/images/pona/lockup.webp" class="responsive-width" alt="Tesco Logo"></div>',
        '<div style="text-align: center; color: white; font-family: var(--font-family-medium);"><span style="font-size: 30px;">Your Clubcard Challenges have finished for now</span> <br/><br/><span style="font-family: var(--font-family-bold); font-size: 20px;">We’re sorry you haven’t been selected this time.<br/> Keep an eye on your inbox for future<br/> Clubcard Challenges.</span></div>',
      ],
    },
  },
  components: {
    header: {
      variant: Variants.VARIANT_2,
      backgroundColor: '#00529F',
      bannerAlternativeText: 'Tesco current challenges banner',
      backgroundImage: {
        mobile: {
          imageUrl: 'assets/images/pona/cover-banner-mobile.webp',
        },
        tablet: {
          imageUrl: 'assets/images/pona/cover-banner-tablet.webp',
        },
        desktop: {
          imageUrl: 'assets/images/pona/cover-banner-desktop.webp',
        },
      },
      mobileRoundel: {
        imageUrl: 'assets/images/pona/roundel.webp',
        alternativeText:
          'Promotional banner: Collect up to {variable} worth of Clubcard points.',
      },
      desktopRoundel: {
        imageUrl: 'assets/images/pona/desktop-roundel.webp',
        alternativeText:
          'Promotional banner: Collect up to {variable} worth of Clubcard points.',
      },
      retailerLogo: {
        imageUrl: 'assets/images/pona/tesco-logo.webp',
        alternativeText: 'Tesco Logo',
      },
      collectUpto: {
        fontFamily: 'var(--font-family-light)',
        label: 'Collect up to',
      },
      maxReward: {
        fontFamily: 'var(--font-family-bold)',
        label: '£50',
      },
      points: {
        fontFamily: 'var(--font-family-medium)',
        label: 'worth of <br/> Clubcard points',
      },
      lockUpPicture: {
        imageUrl: 'assets/images/pona/lockup.webp',
        alternativeText:
          'Tesco Clubcard Challenges – Earn rewards with Tesco, Every little helps.',
      },
      campaignDates: {
        color: 'white',
        fontFamily: 'var(--font-family-bold)',
        template: '{variable}-{variable}',
      },
    },
    brands: {
      variant: Variants.VARIANT_1,
      brandsBgColor: '',
      alternativeText: '',
    },
    footer: {
      variant: Variants.VARIANT_1,
      mobileVisiblity: false,
      styles: {
        display: 'flex',
        padding: '30px 0',
        alignItems: 'center',
        flexDirection: 'row',
        justifyContent: 'space-evenly',
        backgroundColor: '#f2f2f2',
      },
      linkStyles: {
        color: '#272f35',
        fontSize: '16px',
        fontFamily: 'var(--font-family-light)',
        textDecoration: 'none',
        lineHeight: '36px',
      },
      links: [
        {
          external: true,
          label: 'Privacy Notice',
          url: 'assets/images/pona/privacy-policy-tesco.pdf',
        },
        {
          url: 'help',
          external: false,
          label: 'Help',
        },
        {
          external: false,
          label: 'Terms and Conditions',
          url: 'help',
        },
      ],
    },
    tutorial: {
      background: 'transparent',
      carousel: {
        config: {
          sectionAriaLabel: 'Tutorial carousel',
          controlsAriaLabel: 'Carousel controls',
          paginationAriaLabel: 'Slide indicators',
          title: {
            fontSize: '18px',
            color: 'white',
            fontFamily: 'var(--font-family)',
            text: "We've handpicked your challenges just for you,<br/> based on products you already know and love",
          },
          dots: {
            activeBg: 'white',
            inactiveBg: 'rgb(0 82 159 / 60%)',
            ariaLabel: 'Go to slide {variable} of {variable}',
          },
          slideIndicatorLabel: 'Slide {variable} on {variable}',
          controls: {
            leftIcon: {
              alternativeText: 'Go to the previous slide',
              imageUrl: 'assets/images/pona/chevron-left.svg',
            },
            rightIcon: {
              alternativeText: 'Go to the next slide',
              imageUrl: 'assets/images/pona/chevron-right.svg',
            },
          },
        },
        items: {
          variant: Variants.VARIANT_3,
          styles: {
            title: {
              color: 'white',
              fontFamily: 'var(--font-family-bold)',
            },
            description: {
              color: 'white',
              fontFamily: 'var(--font-family-light)',
            },
            card: {
              border: 'none',
              boxShadow: 'none',
              borderRadius: '0',
              background:
                'transparent center / contain no-repeat url("assets/images/pona/step-item-bg-image.webp")',
            },
          },
          steps: [
            {
              title: 'Choose',
              description:
                'Pick up to 10 challenges<br>from a selection of brands<br>you love',
            },
            {
              title: 'Shop',
              description:
                'Simply shop in-store or<br>online to hit your<br>challenges’ targets',
            },
            {
              title: 'Reward',
              description:
                'Collect even more<br>Clubcard points with<br>every completed<br>challenge',
            },
          ],
        },
      },
      closeWrapper: {
        default:
          '<div style="display: flex; align-items: center;"><img style="max-width: 20px; margin-right: 5px" src="assets/images/pona/chevron-down.svg" alt=""><span style="color: white;text-decoration: underline;">You can hide this tutorial</span></div>',
        closed:
          '<div style="display: flex; align-items: center;"><img style="max-width: 30px; margin-right: 5px; transform: rotate(180deg);" src="assets/images/pona/chevron-down.svg"alt=""></div>',
      },
      borderBottom: '1px solid white',
    },
    navbar: {
      variant: Variants.VARIANT_1,
      menuVariant: Variants.VARIANT_1,
      hasLayoutPaddingTop: false,
      dropdownLabel: 'Menu',
      fontFamily: 'var(--font-family-bold)',
      openButton: {
        imageUrl: 'assets/images/pona/menu.svg',
        buttonTitle: 'Click to see more options',
        imageAlt: 'Click to see more options',
      },
      logo: {
        url: 'current',
        height: '30px',
        imageUrl: 'assets/images/pona/tesco-logo.webp',
        alternativeText: 'Tesco logo',
      },
      items: [
        {
          url: 'current',
          label: 'My challenges',
          ariaLabel: 'Go to the Tesco challenges page',
          menuIcon: {
            imageUrl: 'assets/images/pona/home.svg',
            alternativeText: 'Home Menu Icon',
          },
          iconUrlSelected: {
            imageUrl: 'assets/images/pona/home-selected.svg',
            alternativeText: 'Home Menu Icon Selected',
          },
          displayOn: [NavbarItemLocation.MOBILE, NavbarItemLocation.DESKTOP],
        },
        {
          url: 'help',
          label: 'Help centre',
          ariaLabel: 'Go to the Tesco help page',
          menuIcon: {
            imageUrl: 'assets/images/pona/help.svg',
            alternativeText: 'Help Menu Icon',
          },
          iconUrlSelected: {
            imageUrl: 'assets/images/pona/help-selected.svg',
            alternativeText: 'Help Menu Icon Selected',
          },
          displayOn: [NavbarItemLocation.MOBILE, NavbarItemLocation.DESKTOP],
        },
        {
          url: 'assets/files/pona/privacy-policy.pdf',
          ariaLabel: 'Go to Tesco’s privacy policy document',
          label: 'Privacy Policy',
          external: true,
          displayOn: [NavbarItemLocation.DROPDOWN],
        },
        {
          url: 'help',
          ariaLabel: 'Go to Tesco’s terms and conditions document',
          external: false,
          label: 'Terms and Conditons',
          displayOn: [NavbarItemLocation.DROPDOWN],
        },
      ],
    },
    wallet: {
      variant: Variants.VARIANT_2,
      backgroundColor: '#00539F',
      label: {
        color: 'white',
        fontSize: '14px',
        text: 'My points',
        lineHeight: '28px',
        mobileText: 'Points',
        fontFamily: 'var(--font-family-medium)',
      },
      amount: {
        color: 'white',
        fontFamily: 'var(--font-family-bold)',
      },
    },
    countdown: {
      variant: Variants.VARIANT_1,
      showOnPages: 'all',
      showAfterCampaignSummaryWhen: [],
      showBeforeCampaignSummaryWhen: [],
      showOnNavbarWhen: [DeviceType.DESKTOP],
      titleAppearing: ['row', 'row', 'row'],
      titleVerticalAlignment: ['center', 'center', 'center'],
      labelStyle: {
        color: '#333333',
        fontFamily: 'var(--font-family)',
      },
      title: {
        color: '#333333',
        showTitleOnMobile: false,
        fontFamily: 'var(--font-family-medium)',
        label: 'Tesco Clubcard Challenges<br/> ends in',
      },
      numberStyle: {
        color: 'white',
        backgroundColor: '#00539F',
        fontFamily: 'var(--font-family-medium)',
      },
      texts: {
        days: 'Days',
        hours: 'Hours',
        minutes: 'Minutes',
        seconds: 'Seconds',
      },
    },
    challenges: {
      elements: {
        winCard: {
          variant: Variants.VARIANT_1,
          backgroundColor: 'white',
          left: {
            winCardImage: {
              imageUrl: 'assets/images/pona/max-reward.webp',
              alternativeText:
                "You've collected {variable} Clubcard points by completing this challenge",
            },
            amount: {
              fontFamily: 'var(--font-family-bold)',
            },
            winFirstLabel: {
              text: "You've <br/>collected",
              color: 'white',
              fontFamily: 'var(--font-family-light)',
            },
            winSecondLabel: {
              text: 'Clubcard <br/>points',
              color: 'white',
              fontFamily: 'var(--font-family-bold)',
            },
          },
          right: {
            winRightLabel: {
              text: 'Challenge <br/> completed',
              color: '#00539F',
              fontFamily: 'var(--font-family-bold)',
            },
            logo: {
              imageUrl: 'assets/images/pona/tesco-club.webp',
              alternativeText: 'Tesco club card logo',
            },
          },
        },
        selectionButton: {
          variant: Variants.VARIANT_3,
          buttonCatchPhrase: {
            buttonLabel: 'Select this challenge',
            buttonBackgroundColor: '#00539F',
            buttonColor: 'white',
            buttonBorderRadius: '65px',
            buttonFontFamily: 'var(--font-family-bold)',
          },
          catchPhrase: {
            collectUpTo: {
              label: 'Collect up to',
              fontSize: '9px',
              lineHeight: '15px',
              fontFamily: 'var(--font-family-light)',
            },
            selectImage: {
              imageUrl: 'assets/images/pona/reward.webp',
              alternativeText:
                'Select this challenge and collect up to {variable} points',
            },
            amountFontFamily: 'var(--font-family-bold)',
            amountColor: '#FFFFFF',
            points: {
              pointsLabel: 'points',
              pointsFontFamily: 'var(--font-family-bold)',
            },
          },
        },
        unselectionButton: {
          variant: Variants.VARIANT_1,
          icon: {
            imageUrl: 'assets/images/pona/cancel-icon.svg',
            alternativeText: 'Cancel this challenge',
          },
        },
        outOfStock: {
          variant: Variants.VARIANT_1,
          borderRadius: '12px',
          border: '2px solid white',
          backgroundColor: 'rgba(5, 23, 89, 0.8)',
          description: {
            text: 'This challenge <br/> was popular, <br/>sorry you missed out',
            textColor: 'white',
            fontFamily: 'var(--font-family-bold)',
            fontSize: '20px',
          },
        },
      },
      step: {
        [SubType.DEFAULT]: {
          variant: Variants.VARIANT_3,
          bannerAlternativeText: 'Challenges banner',
          logoAlternativeText: 'Logo banner',
          selectionButtonbackgroundColor: 'white',
          cardStyles: {
            border: '2px solid #ffffff',
            boxShadow: '0px 0px 24px rgba(255, 255, 255, 0.9)',
          },
          header: {
            height: '143px',
            backgroundColor: '#FFFFFF',
          },
          subHeader: {
            color: '#272F35',
            completedBackgroundColor: 'white',
            backgroundColor: 'white',
            fontFamily: 'var(--font-family-bold)',
            fontSize: '10px',
            icon: {
              title: 'Click to see the list of products',
              displayWhenSelected: true,
              imageUrl: 'assets/images/pona/info.svg',
              alternativeText: 'Click to see the list of products',
            },
          },
          body: {
            progressbar: {
              spendLabel: 'Spend',
              earnLabel: 'Collect',
              labelColor: '#272F35',
              progressBarLeft: '42px',
              progressColor: '#00539F',
              containerColor: '#D1D4D3',
              labelFontFamily: 'var(--font-family)',
              pictureStageReached: {
                imageUrl: 'assets/images/pona/picture-reached.svg',
                alternativeText: 'Step reached: Marking this step as completed',
              },
            },
            rewards: {
              activeLabel: 'points',
              passedLabel: 'points',
              flexDirection: 'column',
              color: '',
              fontSize: '18px',
              fontFamily: 'var(--font-family-bold)',
              backgroundColors: {
                bubbleWidth: '55px',
                bubbleHeight: '67px',
                reached: {
                  fill: '#00539F',
                  stroke: '#00539F',
                  textColor: 'white',
                  labelColor: 'white',
                },
                unreached: {
                  fill: '#white',
                  stroke: '#00539F',
                  textColor: '#00539F',
                  labelColor: '#00539F',
                },
                default: {
                  fill: '#F2F2F2',
                  stroke: '#F2F2F2',
                  textColor: ' #bababa',
                  labelColor: ' #bababa',
                },
                backgroundImage: 'assets/images/pona/reward-default.svg',
                alternativeText: {
                  reached: 'I collected {variable} points',
                  default: 'I collected {variable} points',
                  unreached: 'Collect {variable} points',
                },
              },
            },
            targets: {
              color: '#272F35',
              fontFamily: 'var(--font-family-medium)',
            },
          },
          footer: {
            color: '#272F35',
            label: "You've spent",
            icon: {
              imageUrl: 'assets/images/pona/shop.svg',
              alternativeText: 'Cart icon',
            },
            fontFamily: 'var(--font-family-light)',
            spentAmountFontFamily: 'var(--font-family-bold)',
          },
        },
      },
      frequency: {
        [SubType.DEFAULT]: {
          variant: Variants.VARIANT_2,
          border: '2px solid #ffffff',
          boxShadow: '0px 0px 24px rgba(255, 255, 255, 0.9)',
          height: '143px',
          header: {
            backgroundImage: {
              url: 'assets/images/pona/frequency-banner.webp',
              bannerAriaLabel:
                'Frequency challenge banner: Shop online or via the Tesco app to complete your challenge',
            },
          },
          subHeader: {
            completedBackgroundColor: 'white',
            backgroundColor: 'white',
            title: {
              color: 'rgb(39, 47, 53)',
              label: 'Minimum spend of £{variable} per visit',
              fontFamily: 'var(--font-family-light)',
            },
          },
          body: {
            firstLabel: {
              color: 'white',
              fontFamily: 'var(--font-family-bold)',
              text: 'Shop Online',
              textAlign: 'start',
              topPosition: '25px',
            },
            secondLabel: {
              color: 'white',
              fontFamily: 'var(--font-family-light)',
              text: 'Shop online or via the <br/> Tesco app to complete <br/>your challenge',
              textAlign: 'start',
              topPosition: '30px',
            },
            rewardRatio: {
              rewardPicture: {
                imageUrl: 'assets/images/pona/frequency-max-reward.svg',
                alternativeText: '',
              },
              fontFamily: 'var(--font-family-bold)',
              color: 'var(--challenge-primary-color)',
            },
          },
        },
      },
    },
    challengeSummary: {
      variant: Variants.VARIANT_3,
      title: {
        color: 'white',
        text: 'My chosen Clubcard Challenges',
        fontFamily: 'var(--font-family-bold)',
      },
      update: {
        color: 'white',
        text: 'Last updated {variable}',
        fontFamily: 'var(--font-family-medium)',
        dateFormat: 'dd/MM/yyyy',
      },
      borderRadius: '60px',
      border: '3px solid white',
      challengeLabels: {
        onGoingLabel: {
          color: 'white',
          backgroundColor: '#00539F33',
          text: 'challenges selected',
          fontFamily: 'var(--font-family-bold)',
        },
        completedLabel: {
          color: 'white',
          backgroundColor: '#00539F33',
          text: 'challenge(s) completed',
          fontFamily: 'var(--font-family-bold)',
        },
      },
      challengesInfos: {
        fontFamily: 'var(--font-family-bold)',
      },
    },
    modals: {
      closeButton: {
        label: 'Close',
        textColor: 'white',
        borderRadius: '50px',
        backgroundColor: '#00539F',
        fontFamily: 'var(--font-family-bold)',
        ariaLabel: 'Close this dialog',
      },
      unselectChallenge: {
        backgroundColor: '#FFFFFF',
        header: {
          logo: {
            alternativeText: 'Logo banner',
          },
          backgroundColor: '',
          backgroundImage: '',
        },
        title: {
          color: '#333333',
          fontFamily: 'var(--font-family-bold)',
          label: 'Are you sure you want to<br/> Unselect this challenge?',
        },
        body: {
          color: '#666666',
          fontFamily: 'var(--font-family-light)',
          label: 'No worries if you regret it you can<br/>select it again !',
        },
        actionButtons: {
          variant: Variants.VARIANT_1,
          confirmButton: {
            label: 'Yes',
            borderRadius: '50px',
            textColor: 'white',
            backgroundColor: '#00529F',
            fontFamily: 'var(--font-family-bold)',
            ariaLabel: 'Confirm cancelling the challenge',
          },
          closeButton: {
            label: 'No',
            textColor: '#00529F',
            borderRadius: '50px',
            backgroundColor: 'white',
            borderColor: '#00529F',
            fontFamily: 'var(--font-family-bold)',
            ariaLabel: 'Cancel the action and return to the challenge',
          },
        },
      },
      productCheck: {
        ariaLabel: '',
        backgroundColor: '#FFFFFF',
        fontFamily: 'var(--font-family-medium)',
        tabs: [
          {
            type: ProductCheckTabType.PRODUCT_LIST,
            label: 'Enter manually',
            searchable: true,
            inputPlaceholder: 'Enter a barcode to search',
            tableTitle: {
              text: 'List of eligible products',
              color: 'var(--challenge-primary-color)',
            },
            columns: {
              name: {
                header: 'Product',
                color: 'var(--challenge-primary-color)',
                fontFamily: 'var(--font-family-light)',
              },
              code: {
                header: 'Barcode',
                color: 'var(--challenge-primary-color)',
                fontFamily: 'var(--font-family-light)',
              },
            },
          },
        ],
      },
      firstSelection: {
        backgroundColor: 'white',
        title: {
          color: '#333333',
          label: 'Challenge selected',
          fontFamily: 'var(--font-family)',
        },
        header: {
          backgroundImage: '',
          backgroundColor: '#F2F2F2',
          logo: {
            imageUrl: 'assets/images/pona/first-selection.svg',
            alternativeText: 'You have selected the first challenge',
          },
        },
        body: {
          color: '#666666',
          fontFamily: 'var(--font-family-light)',
          label:
            'Great choice! Fancy another for more points? <br/> You can pick another {variable} challenge(s). ',
        },
      },
      lastSelection: {
        backgroundColor: '#f2f2f2',
        title: {
          color: '#333333',
          fontFamily: 'var(--font-family-bold)',
          label: "That's {variable} challenges",
        },
        header: {
          backgroundImage: '',
          backgroundColor: '#00539F',
          logo: {
            imageUrl: 'assets/images/pona/last-selection.svg',
            alternativeText:
              'Simply shop in-store or online to hit your targets and collect up to {variable} worth of Clubcard points.',
          },
        },
        body: {
          color: '#333333',
          fontFamily: 'var(--font-family)',
          label:
            'Shop in-store or online at Tesco to hit your targets and collect up to £50 worth of Clubcard points.',
        },
      },
      maximumSelection: {
        backgroundColor: '#f2f2f2',
        title: {
          color: '#272f35',
          fontFamily: 'var(--font-family-medium)',
          label: "You've already got {variable} challenges on the go",
        },
        header: {
          backgroundImage: '',
          backgroundColor: '#AB2328',
          logo: {
            imageUrl: 'assets/images/pona/maximum-selection.svg',
            alternativeText:
              'You already have {variable} active challenges. To choose another, unselect one you haven’t started yet.',
          },
        },
        body: {
          color: '#6b767c',
          fontFamily: 'var(--font-family)',
          label:
            'If you’d like to select another challenge, unselect a challenge you haven’t yet started and pick again.',
        },
      },
      outOfStockIssue: {
        backgroundColor: '#FFFFFF',
        title: {
          color: '#272f35',
          fontFamily: 'var(--font-family-bold)',
          label: 'Selection limit reached',
        },
        body: {
          color: '#6b767c',
          fontFamily: 'var(--font-family)',
          label:
            'This challenge has reached the maximum number of participants. Please choose another challenge.',
        },
      },
      genericError: {
        backgroundColor: '#FFFFFF',
        title: {
          color: '#272f35',
          fontFamily: 'var(--font-family-bold)',
          label: 'Error',
        },
        body: {
          color: '#6b767c',
          fontFamily: 'var(--font-family)',
          label:
            "An error occurred, and the challenge couldn't be selected. Please check your connection and try again.",
        },
      },
      timeoutError: {
        backgroundColor: '#FFFFFF',
        title: {
          color: '#272f35',
          fontFamily: 'var(--font-family-bold)',
          label: 'Request timed out',
        },
        body: {
          color: '#6b767c',
          fontFamily: 'var(--font-family)',
          label:
            'Selecting the challenge took too long. Please check your connection and try again.',
        },
      },
      challengeUnavailable: {
        backgroundColor: '#FFFFFF',
        title: {
          color: '#272f35',
          fontFamily: 'var(--font-family-bold)',
          label: 'Campaign ended',
        },
        body: {
          color: '#6b767c',
          fontFamily: 'var(--font-family)',
          label:
            'This campaign has ended and challenges are no longer available for selection.',
        },
      },
    },
  },
};
